import React, { useState, useEffect } from 'react';
import { X, Wallet, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import apiService from '../../apiService';
import { settingsAPI } from '../../services/api';

const QUICK_AMOUNTS = [100, 250, 500, 1000];

const PAYMENT_METHODS = [
  {
    id: 'upi',
    label: 'UPI',
    subtitle: 'Google Pay · PhonePe · Paytm · BHIM',
    icon: '📲',
    razorpayMethod: 'upi',
    color: '#7c3aed',
    bg: '#f5f3ff',
  },
  {
    id: 'card',
    label: 'Credit / Debit Card',
    subtitle: 'Visa · Mastercard · RuPay · Amex',
    icon: '💳',
    razorpayMethod: 'card',
    color: '#2563eb',
    bg: '#eff6ff',
  },
  {
    id: 'netbanking',
    label: 'Net Banking',
    subtitle: 'SBI · HDFC · ICICI · Axis & more',
    icon: '🏦',
    razorpayMethod: 'netbanking',
    color: '#0891b2',
    bg: '#ecfeff',
  },
  {
    id: 'wallet',
    label: 'Mobile Wallet',
    subtitle: 'Paytm · Amazon Pay · Mobikwik',
    icon: '👛',
    razorpayMethod: 'wallet',
    color: '#059669',
    bg: '#ecfdf5',
  },
  {
    id: 'store_qr',
    label: 'Pay via Store QR',
    subtitle: 'Scan store UPI QR · Pay · Enter UTR',
    icon: '🏪',
    razorpayMethod: null,
    color: '#0f766e',
    bg: '#f0fdfa',
  },
];

const WalletTopupModal = ({ isOpen, onClose, onSuccess, appSettings, razorpayKey }) => {
  const [amount, setAmount]                 = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  // steps: 'enter' → 'method' → 'store_qr' → 'utr_submitted' → 'success'
  const [step, setStep]                     = useState('enter');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [utrInput, setUtrInput]             = useState('');
  const [newBalance, setNewBalance]         = useState(0);
  // Live settings fetched fresh every time the modal opens
  const [liveSettings, setLiveSettings]     = useState(null);

  // Re-fetch settings from backend every time the modal opens so payment
  // method toggles always reflect the latest admin configuration.
  useEffect(() => {
    if (!isOpen) return;
    settingsAPI.getSettings()
      .then((resp) => {
        const s = resp?.settings || resp;
        if (s && typeof s === 'object') {
          setLiveSettings({
            upiId:               s.upi_id        ?? appSettings?.upiId    ?? '',
            upiQrCode:           s.upi_qr_code   ?? appSettings?.upiQrCode ?? `${process.env.PUBLIC_URL}/static/upi-qr.jpeg`,
            pmUpiEnabled:        s.pm_upi_enabled        !== undefined ? !!s.pm_upi_enabled        : (appSettings?.pmUpiEnabled        ?? true),
            pmCardEnabled:       s.pm_card_enabled       !== undefined ? !!s.pm_card_enabled       : (appSettings?.pmCardEnabled       ?? true),
            pmNetbankingEnabled: s.pm_netbanking_enabled !== undefined ? !!s.pm_netbanking_enabled : (appSettings?.pmNetbankingEnabled ?? true),
            pmWalletEnabled:     s.pm_wallet_enabled     !== undefined ? !!s.pm_wallet_enabled     : (appSettings?.pmWalletEnabled     ?? true),
            pmStoreQrEnabled:    s.pm_store_qr_enabled   !== undefined ? !!s.pm_store_qr_enabled   : (appSettings?.pmStoreQrEnabled   ?? true),
          });
        }
      })
      .catch(() => { /* fallback to appSettings below */ });
  }, [isOpen]);

  // Merge: live settings take priority over cached appSettings; fallback to defaults
  const cfg = liveSettings || appSettings || {};
  const storeUpiId = cfg.upiId    || '';
  const storeQrUrl = cfg.upiQrCode || `${process.env.PUBLIC_URL}/static/upi-qr.jpeg`;

  // Filter payment methods based on admin-controlled toggles (default all enabled)
  const enabledMethods = PAYMENT_METHODS.filter((m) => {
    if (m.id === 'upi')        return cfg.pmUpiEnabled        !== false;
    if (m.id === 'card')       return cfg.pmCardEnabled       !== false;
    if (m.id === 'netbanking') return cfg.pmNetbankingEnabled !== false;
    if (m.id === 'wallet')     return cfg.pmWalletEnabled     !== false;
    if (m.id === 'store_qr')   return cfg.pmStoreQrEnabled   !== false;
    return true;
  });

  if (!isOpen) return null;

  const handleQuickAmount = (val) => { setAmount(String(val)); setError(''); };

  const handleAmountChange = (e) => {
    setAmount(e.target.value.replace(/[^0-9]/g, ''));
    setError('');
  };

  const handleProceed = () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10)  { setError('Minimum top-up amount is ₹10'); return; }
    if (amt > 10000)       { setError('Maximum top-up amount is ₹10,000'); return; }
    setError('');
    setStep('method');
  };

  const handlePay = async (methodOverride) => {
    const method = methodOverride || selectedMethod;
    if (!method) { setError('Please select a payment method'); return; }
    const amt = parseFloat(amount);
    setLoading(true);
    setError('');

    try {
      const initiateResp = await apiService.initiateWalletTopup(amt, 'razorpay');
      if (!initiateResp?.success) throw new Error(initiateResp?.data?.message || 'Failed to initiate top-up');

      const { gateway_order_id, pending_transaction_id } = initiateResp.data ?? initiateResp;

      const afterPayment = async (razorpayResponse) => {
        try {
          const verifyResp = await apiService.verifyWalletTopup({
            pending_transaction_id,
            transaction_id: razorpayResponse.razorpay_payment_id,
            gateway_response: razorpayResponse,
          });
          if (verifyResp?.success) {
            const balance = verifyResp.data?.new_balance ?? 0;
            setNewBalance(balance);
            setStep('success');
            onSuccess && onSuccess(balance);
          } else {
            setError(verifyResp?.data?.message || 'Payment verification failed');
            setStep('method');
          }
        } catch {
          setError('Payment verification failed. Please contact support.');
          setStep('method');
        }
        setLoading(false);
      };

      if (!window.Razorpay) {
        setError('Payment gateway failed to load. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const rzKey = razorpayKey;
      if (!rzKey) {
        setError('Razorpay public key is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      const options = {
        key:         rzKey,
        amount:      amt * 100,
        currency:    'INR',
        name:        'Deal Store',
        description: 'Wallet Top-up',
        order_id:    gateway_order_id,
        method:      method.razorpayMethod,
        handler:     afterPayment,
        prefill:     { name: '', email: '', contact: '' },
        theme:       { color: method.color },
        modal:       { ondismiss: () => setLoading(false) },
      };

      new window.Razorpay(options).open();
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    setStep('enter');
    setSelectedMethod(null);
    setUtrInput('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[300]"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={handleClose}
    >
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t"
        style={{ maxWidth: 448, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: 301 }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── STEP: enter amount ──────────────────────────────────── */}
        {step === 'enter' && (
          <div className="p-6 pb-8">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded bg-[#fff8fa] flex items-center justify-center">
                  <Wallet size={20} className="text-[#ff3f6c]" />
                </div>
                <h2 className="text-lg font-bold text-[#282c3f]">Add Money to Wallet</h2>
              </div>
              <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-[#94969f]" />
              </button>
            </div>

            <p className="text-xs font-semibold text-[#94969f] uppercase tracking-wide mb-2">Quick Select</p>
            <div className="grid grid-cols-4 gap-2 mb-5">
              {QUICK_AMOUNTS.map((val) => (
                <button key={val} onClick={() => handleQuickAmount(val)}
                  className="py-2.5 rounded text-sm font-bold border transition-all"
                  style={amount === String(val)
                    ? { borderColor: '#2563eb', background: '#eff6ff', color: '#2563eb' }
                    : { borderColor: '#e5e7eb', color: '#374151' }}
                >
                  ₹{val}
                </button>
              ))}
            </div>

            <p className="text-xs font-semibold text-[#94969f] uppercase tracking-wide mb-2">Or enter amount</p>
            <div className="relative mb-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94969f] font-bold text-xl">₹</span>
              <input
                type="number" min="10" max="10000" value={amount} onChange={handleAmountChange}
                placeholder="0"
                className="w-full border border-[#eaeaec] rounded pl-9 pr-4 py-3 text-2xl font-bold focus:outline-none focus:border-[#ff3f6c] text-[#282c3f]"
              />
            </div>
            <p className="text-xs text-[#94969f] mb-4">Min ₹10 · Max ₹10,000</p>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded px-3 py-2 mb-4 text-sm">
                <AlertCircle size={16} />{error}
              </div>
            )}

            <button onClick={handleProceed} disabled={!amount}
              className="w-full py-3.5 rounded font-bold text-base text-white transition-all disabled:opacity-40"
              style={{ background: 'linear-gradient(90deg,#2563eb,#1d4ed8)' }}
            >
              Proceed to Pay {amount ? `₹${amount}` : ''}
            </button>
          </div>
        )}

        {/* ── STEP: select payment method ─────────────────────────── */}
        {step === 'method' && (
          <div className="p-6 pb-8">
            <div className="flex items-center gap-3 mb-5">
              <button onClick={() => { setStep('enter'); setSelectedMethod(null); setError(''); }}
                className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowLeft size={20} className="text-[#535766]" />
              </button>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-[#282c3f]">Select Payment Method</h2>
                <p className="text-xs text-[#94969f]">Adding <span className="font-bold text-[#ff3f6c]">₹{amount}</span> to your wallet</p>
              </div>
              <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-[#94969f]" />
              </button>
            </div>

            {/* Amount pill */}
            <div className="flex items-center gap-3 bg-[#fff8fa] border border-[#eaeaec] rounded px-4 py-3 mb-5">
              <Wallet size={20} className="text-[#ff3f6c] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-[#94969f]">Amount to be added</p>
                <p className="text-xl font-extrabold text-[#ff3f6c]">₹{parseFloat(amount).toFixed(2)}</p>
              </div>
              <span className="text-xs bg-[#fff8fa] text-[#ff3f6c] font-semibold px-2 py-1 rounded-lg">Secure</span>
            </div>

            <p className="text-xs font-semibold text-[#94969f] uppercase tracking-wide mb-3">Choose how to pay</p>
            <div className="space-y-3 mb-5">
              {enabledMethods.length === 0 && (
                <p className="text-sm text-[#94969f] text-center py-4">No payment methods are currently available. Please contact support.</p>
              )}
              {enabledMethods.map((method) => {
                const isSelected   = selectedMethod?.id === method.id;
                const isStoreQR    = method.id === 'store_qr';
                return (
                  <button key={method.id}
                    onClick={() => {
                      setSelectedMethod(method);
                      setError('');
                      if (isStoreQR) { setUtrInput(''); setStep('store_qr'); }
                    }}
                    className="w-full flex items-center gap-4 rounded p-4 border transition-all text-left"
                    style={{
                      borderColor: isSelected ? method.color : '#e5e7eb',
                      background:  isSelected ? method.bg   : '#fff',
                      boxShadow:   isSelected ? `0 0 0 3px ${method.color}22` : 'none',
                    }}
                  >
                    <div className="w-11 h-11 rounded flex items-center justify-center text-2xl flex-shrink-0"
                      style={{ background: method.bg }}>
                      {method.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#282c3f] text-sm">{method.label}</p>
                      <p className="text-xs text-[#94969f] truncate">{method.subtitle}</p>
                    </div>
                    {isStoreQR ? (
                      <span className="text-xs font-semibold px-2 py-1 rounded-lg flex-shrink-0"
                        style={{ background: method.bg, color: method.color }}>
                        Open →
                      </span>
                    ) : (
                      <div className="w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all"
                        style={{ borderColor: isSelected ? method.color : '#d1d5db', background: isSelected ? method.color : 'transparent' }}>
                        {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded px-3 py-2 mb-4 text-sm">
                <AlertCircle size={16} />{error}
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-xs text-[#94969f] mb-4">
              <span>🔒</span> 256-bit encrypted · Powered by Razorpay
            </div>

            <button
              onClick={() => handlePay()}
              disabled={loading || !selectedMethod || selectedMethod?.id === 'store_qr'}
              className="w-full py-4 rounded font-bold text-base text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: selectedMethod && selectedMethod.id !== 'store_qr' ? `linear-gradient(90deg,${selectedMethod.color},${selectedMethod.color}cc)` : '#9ca3af' }}
            >
              {loading ? (
                <><span className="w-5 h-5 border border-white border-t-transparent rounded-full animate-spin" />Processing…</>
              ) : (
                <>{selectedMethod ? selectedMethod.icon : '💰'} Pay ₹{parseFloat(amount).toFixed(2)}{selectedMethod && selectedMethod.id !== 'store_qr' ? ` via ${selectedMethod.label}` : ''}</>
              )}
            </button>
          </div>
        )}

        {/* ── STEP: store UPI QR + UTR entry ─────────────────────── */}
        {step === 'store_qr' && (
          <div className="p-6 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => { setStep('method'); setSelectedMethod(null); setUtrInput(''); setError(''); }}
                className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowLeft size={20} className="text-[#535766]" />
              </button>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-[#282c3f]">Pay via Store QR</h2>
                <p className="text-xs text-[#94969f]">Scan · Pay ₹{amount} · Enter UTR</p>
              </div>
              <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-[#94969f]" />
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-teal-50 border border-teal-200 rounded px-4 py-3 mb-4">
              <div className="flex flex-col gap-1 text-sm text-teal-800">
                {['Open any UPI app (GPay · PhonePe · Paytm)', `Scan the QR below and pay ₹${amount}`, 'Come back and enter your UTR / Ref number'].map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="font-extrabold text-teal-600 flex-shrink-0">{i + 1}.</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* QR image */}
            <div className="flex flex-col items-center mb-4">
              <div className="rounded border border-teal-200 bg-white p-3 shadow-md">
                <img
                  src={storeQrUrl}
                  alt="Store UPI QR Code"
                  className="w-52 h-52 object-contain rounded"
                  onError={(e) => { e.target.src = `${process.env.PUBLIC_URL}/upi-qr.png`; }}
                />
              </div>
              {storeUpiId && (
                <div className="mt-3 flex items-center gap-2 bg-gray-50 border border-[#eaeaec] rounded px-4 py-2">
                  <span className="text-xs text-[#94969f] font-medium">UPI ID:</span>
                  <span className="text-sm font-bold text-[#282c3f]">{storeUpiId}</span>
                  <button
                    onClick={() => navigator.clipboard?.writeText(storeUpiId)}
                    className="text-xs text-teal-600 font-semibold ml-1 hover:underline"
                  >
                    Copy
                  </button>
                </div>
              )}
              <div className="mt-2 text-xs font-bold text-[#282c3f] bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                Pay exactly ₹{parseFloat(amount).toFixed(2)}
              </div>
            </div>

            {/* UTR input */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-[#535766] mb-1.5">
                UTR / Transaction Reference Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={utrInput}
                onChange={(e) => { setUtrInput(e.target.value.toUpperCase()); setError(''); }}
                placeholder="e.g. 123456789012"
                className="w-full border border-[#eaeaec] rounded px-4 py-3 text-sm font-bold tracking-wider focus:outline-none focus:border-teal-500 text-[#282c3f]"
                maxLength={50}
              />
              <p className="text-xs text-[#94969f] mt-1">Found in your UPI app under transaction details</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded px-3 py-2 mb-4 text-sm">
                <AlertCircle size={16} />{error}
              </div>
            )}

            <button
              disabled={loading || utrInput.trim().length < 6}
              onClick={async () => {
                setLoading(true);
                setError('');
                try {
                  const resp = await apiService.submitUpiQrTopup(parseFloat(amount), utrInput.trim());
                  if (resp?.success) {
                    setStep('utr_submitted');
                  } else {
                    setError(resp?.data?.message || resp?.data?.errors?.utr?.[0] || 'Submission failed. Please try again.');
                  }
                } catch {
                  setError('Something went wrong. Please try again.');
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full py-4 rounded font-bold text-base text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
              style={{ background: 'linear-gradient(90deg,#0f766e,#0d9488)' }}
            >
              {loading
                ? <><span className="w-5 h-5 border border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
                : <>✅ I've Paid — Submit UTR</>}
            </button>
          </div>
        )}

        {/* ── STEP: UTR submitted ─────────────────────────────────── */}
        {step === 'utr_submitted' && (
          <div className="p-6 pb-8 text-center">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span style={{ fontSize: 44 }}>⏳</span>
            </div>
            <h3 className="text-xl font-extrabold text-[#282c3f] mb-2">Payment Under Review</h3>
            <p className="text-[#94969f] text-sm mb-1">
              Your UPI payment of <span className="font-bold text-teal-700">₹{parseFloat(amount).toFixed(2)}</span> has been submitted.
            </p>
            <p className="text-[#94969f] text-xs mb-5">
              Your wallet will be credited within <strong>30 minutes</strong> after our team verifies your transaction.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded px-4 py-3 mb-5 text-left space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#94969f]">Amount</span>
                <span className="font-bold text-teal-700">₹{parseFloat(amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#94969f]">UTR Submitted</span>
                <span className="font-bold text-[#535766] tracking-wide">{utrInput}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#94969f]">Status</span>
                <span className="font-semibold text-amber-600">Pending Verification</span>
              </div>
            </div>
            <button onClick={handleClose}
              className="w-full py-3.5 rounded font-bold text-base text-white"
              style={{ background: 'linear-gradient(90deg,#0f766e,#0d9488)' }}>
              Done
            </button>
          </div>
        )}

        {/* ── STEP: success ───────────────────────────────────────── */}
        {step === 'success' && (
          <div className="p-6 pb-8 text-center">
            <div className="w-20 h-20 bg-[#e8f7f3] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h3 className="text-xl font-extrabold text-[#282c3f] mb-1">Money Added!</h3>
            <p className="text-[#94969f] mb-1">
              ₹{parseFloat(amount).toFixed(2)} added to your wallet
              {selectedMethod && <span> via {selectedMethod.label}</span>}
            </p>
            <div className="my-4 py-4 px-6 bg-[#fff8fa] rounded border border-[#eaeaec] w-full">
              <p className="text-xs text-[#94969f] mb-0.5">New Wallet Balance</p>
              <p className="text-3xl font-extrabold text-[#ff3f6c]">₹{newBalance.toFixed(2)}</p>
            </div>
            <button onClick={handleClose}
              className="w-full bg-[#ff3f6c] text-white py-3.5 rounded font-bold hover:bg-[#e03a60] transition-colors">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletTopupModal;
